import {assertUnreachable} from 'common/util/typescript';
import {EventFrequencyClause} from 'common/query-builder/cohorts-query-builder/models/builder/event-frequency-clause';
import {PeoplePropertyClause} from 'common/query-builder/cohorts-query-builder/models/builder/people-property-clause';
import {PropertyFilter} from 'common/query-builder/cohorts-query-builder/models/builder/property-filter';
import {PropertyFilter as CommonPropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {
  BehaviorCountType,
  LogicalOperator,
  Property,
} from 'common/query-builder/cohorts-query-builder/models/enums/expression-enums';
import {
  DatetimeOperator,
  FrequencyOperator,
} from 'common/query-builder/cohorts-query-builder/models/enums/builder-enums';
import {ClauseOperators} from 'common/query-builder/cohorts-query-builder/models/types/builder-state-types';
import {FiltersOperator} from 'common/query-builder/query-entry-section/types';
import {
  BooleanOperator,
  FilterOperand,
  FilterOperator as CommonFilterOperator,
  FilterValue as CommonFilterValue,
  ListOperator,
  NumberOperator,
  StringOperator,
} from 'common/widgets/property-filter-menu/models/types';
import {EVENTS, PRIMITIVE_MATH_TYPES, PropertyType, ResourceType, SPECIAL_EVENTS} from 'common/report/constants';
import {renameEvent} from 'common/report/util/events';
import {CustomPropertyResourceType} from 'common/report/stores/custom-properties';
import {CustomProperty} from 'common/types/reports/query';
import {DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {customEventsStore} from 'common/report/stores/custom-events';
import {EventItem} from 'common/query-builder/query-block/events-menu/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {valueToSerializedDatetime} from 'common/query-builder/cohorts-query-builder/util/staged-cohort';
import {NUMBER_OPERATOR_NEGATION_MAP, STRING_OPERATOR_NEGATION_MAP} from 'common/report/insights/util';
import {SerializedCohort} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import {CohortSegment} from 'common/query-builder/query-block/segment-menu/types';
import {MPProperties} from 'common/constants';

import {
  convertSerializedDatetimeToDateRange,
  DatetimeOperatorToDateRangeTypeMapping,
  getDateRangeFromBehavior,
  getDateRangeLabelFromBehavior,
} from './time';

type FilterNegationModifier = (propertyFilter: CommonPropertyFilter) => CommonPropertyFilter;

export const reversibleFilterOperator: (
  a: CommonFilterOperator,
  b: CommonFilterOperator,
) => {[key: string]: FilterNegationModifier} = (a, b) => ({
  [a]: (propertyFilter) =>
    new CommonPropertyFilter({
      ...propertyFilter.getAttributes(),
      filterOperator: b,
    }),
  [b]: (propertyFilter) =>
    new CommonPropertyFilter({
      ...propertyFilter.getAttributes(),
      filterOperator: a,
    }),
});

export const FilterOperatorNegationMapping: {[key: string]: FilterNegationModifier} = {
  ...reversibleFilterOperator(StringOperator.Contains, STRING_OPERATOR_NEGATION_MAP[StringOperator.Contains]),
  ...reversibleFilterOperator(StringOperator.Equals, STRING_OPERATOR_NEGATION_MAP[StringOperator.Equals]),
  ...reversibleFilterOperator(StringOperator.Set, STRING_OPERATOR_NEGATION_MAP[StringOperator.Set]),

  ...reversibleFilterOperator(NumberOperator.EqualTo, NUMBER_OPERATOR_NEGATION_MAP[NumberOperator.EqualTo]),
  ...reversibleFilterOperator(NumberOperator.Between, NUMBER_OPERATOR_NEGATION_MAP[NumberOperator.Between]),
  ...reversibleFilterOperator(NumberOperator.AtLeast, NUMBER_OPERATOR_NEGATION_MAP[NumberOperator.LessThan]),
  ...reversibleFilterOperator(NumberOperator.GreaterThan, NUMBER_OPERATOR_NEGATION_MAP[NumberOperator.GreaterThan]),

  ...reversibleFilterOperator(BooleanOperator.True, BooleanOperator.False),
  ...reversibleFilterOperator(ListOperator.Contains, ListOperator.DoesNotContain),

  ...reversibleFilterOperator(DateRangeType.Between, DateRangeType.NotBetween),
  ...reversibleFilterOperator(DateRangeType.On, DateRangeType.NotOn),
  ...reversibleFilterOperator(DateRangeType.RelativeAfter, DateRangeType.NotRelativeAfter),
  ...reversibleFilterOperator(DateRangeType.Since, DateRangeType.Before),

  [DateRangeType.RelativeFuture]: (propertyFilter) => {
    const {
      window: {unit, value},
    } = propertyFilter.filterValue as Partial<DateRange>;
    /**
     * in the next X units gets negated by applying
     * a rolling `not between` filter
     */
    return new CommonPropertyFilter({
      ...propertyFilter.getAttributes(),
      filterOperator: DateRangeType.NotBetween,
      filterValue: new DateRange({
        type: DateRangeType.NotBetween,
        rolling: true,
        from: {value: 0, unit},
        to: {value: value * -1, unit},
      }),
    });
  },

  /**
   * @todo: [pablo] `not more than X units` has an unexpected behavior
   * in the underlying arb selector, this filter would get negated
   * with a rolling `since` filter, this does not yield the same results
   */
  /** @todo [pablo]: figure out how to display this in the UI */
  [DateRangeType.RelativeBefore]: (propertyFilter) => {
    const {
      window: {unit, value},
    } = propertyFilter.filterValue as Partial<DateRange>;

    return new CommonPropertyFilter({
      ...propertyFilter.getAttributes(),
      filterOperator: DateRangeType.Since,
      filterValue: new DateRange({
        type: DateRangeType.Since,
        rolling: true,
        from: {value, unit},
        to: null,
      }),
    });
  },
};

export const PropertyTypeToResourceTypeMapping: {[key: string]: ResourceType} = {
  [Property.Event]: ResourceType.Event,
  [Property.Cohort]: ResourceType.Cohort,
  [Property.User]: ResourceType.User,
};

export function getResourceTypeFromPropertyType(propertyType: Property): ResourceType {
  if (!PropertyTypeToResourceTypeMapping[propertyType]) {
    throw new Error(`Invalid property type ${propertyType} for resourceType conversion`);
  }

  return PropertyTypeToResourceTypeMapping[propertyType];
}

export function getFiltersOperatorFromLiteralClauseOperators(clauseOperators: ClauseOperators): FiltersOperator {
  // return `and` as a default for single clause literals
  if (!clauseOperators) {
    return FiltersOperator.And;
  }

  const [baseOperator, extendedOperator] = clauseOperators;

  if (baseOperator === null) {
    return FiltersOperator.Then;
  }

  if (baseOperator === LogicalOperator.And) {
    return extendedOperator === LogicalOperator.Or ? FiltersOperator.AndAny : FiltersOperator.And;
  }

  if (baseOperator === LogicalOperator.Or) {
    return extendedOperator === LogicalOperator.Or ? FiltersOperator.Or : FiltersOperator.OrAll;
  }

  throw new Error(`Invalid clauseOperators ${clauseOperators}`);
}

export function getGroupingOperatorFromLogicalOperators(
  operators: LogicalOperator[],
): Array<FiltersOperator.And | FiltersOperator.Or> {
  return operators.map((operator) => (operator === LogicalOperator.And ? FiltersOperator.And : FiltersOperator.Or));
}

export function getFiltersOperatorFromPropertyFilterOperator(propertyFilterOperator: LogicalOperator): FiltersOperator {
  if (propertyFilterOperator === LogicalOperator.And) {
    return FiltersOperator.And;
  }

  if (propertyFilterOperator === LogicalOperator.Or) {
    return FiltersOperator.Or;
  }

  throw new Error(`Invalid property filter operator ${propertyFilterOperator}`);
}

export const FrequencyOperatorToNumericFilterOperatorMapping: {[key in FrequencyOperator]: NumberOperator} = {
  [FrequencyOperator.AtLeast]: NumberOperator.AtLeast,
  [FrequencyOperator.Between]: NumberOperator.Between,
  [FrequencyOperator.EqualTo]: NumberOperator.EqualTo,
  [FrequencyOperator.GreaterThan]: NumberOperator.GreaterThan,
  [FrequencyOperator.LessThan]: NumberOperator.LessThan,
};

export function convertPropertyFilterToCommon(filter: PropertyFilter, forceNegated = false): CommonPropertyFilter {
  let filterValue: CommonFilterValue = filter.value;
  let filterOperator: CommonFilterOperator;

  // Transform filter types
  switch (filter.type) {
    case PropertyType.Boolean:
      filterOperator = filter.operator as BooleanOperator;
      break;
    case PropertyType.Datetime:
      filterOperator = DatetimeOperatorToDateRangeTypeMapping[filter.operator as DatetimeOperator];
      filterValue = convertSerializedDatetimeToDateRange(
        valueToSerializedDatetime(filter.value, filter.operator as DatetimeOperator),
        filter.operator as DatetimeOperator,
      );
      break;
    case PropertyType.List:
      filterOperator = filter.operator as ListOperator;
      break;
    case PropertyType.String:
      filterOperator = filter.operator as StringOperator;
      break;
    case PropertyType.Number:
      filterOperator = filter.operator as NumberOperator;
      break;
    case PropertyType.Dimension:
      filterOperator = filter.operator as any;
      break;
    default:
      throw new Error(`Unhandled property filter type in filter ${JSON.stringify({filter})}`);
  }

  const propertyFilter = new CommonPropertyFilter({
    filterOperator,
    filterValue,
    propertyName: filter.property,
    ...(filter.customPropertyId
      ? {
          customPropertyId: filter.customPropertyId,
        }
      : {}),
    ...(filter.scdEvent
      ? {
          scdEvent: filter.scdEvent,
        }
      : {}),
    propertyType: filter.type,
    resourceType: getResourceTypeFromPropertyType(filter.propertyType),
    propertyDefaultType: filter.type,
    dataGroupId: filter.dataGroupId,
    joinPropertyType: filter.joinPropertyType as PropertyType,
  });

  // Operator negations are only applied to filters under People property clauses
  // Event frequency clauses are negated with a frequency of 0 and cohort clauses have negated properties built-in
  return forceNegated || filter.isNegated()
    ? FilterOperatorNegationMapping[filterOperator](propertyFilter)
    : propertyFilter;
}

export function getEventFromCohortEvent(event): EventItem {
  if (!event) {
    return {
      value: SPECIAL_EVENTS.ANY_EVENT,
      label: renameEvent(EVENTS[SPECIAL_EVENTS.ANY_EVENT]),
    };
  }

  if (event.startsWith(`$custom_event:`)) {
    const id = Number(event.replace(/^\$custom_event:/, ``));

    return {
      id,
      custom: true,
      label: customEventsStore?.value[id]?.name ?? event,
      value: event,
    };
  }

  return {label: renameEvent(event), value: event};
}

export function getCohortEventFromEventItem(event: EventItem): string {
  if (event.value === SPECIAL_EVENTS.ANY_EVENT) {
    return ``;
  }

  if (event.custom) {
    return `$custom_event:${event.id}`;
  }

  return event.value;
}

export function getEventFiltersFromEventsFrequencyClause(clause: EventFrequencyClause): CommonPropertyFilter[] {
  const {eventSelector, type} = clause.behavior.count;

  const additionalFilters = EventFrequencyClause.getPropertyFiltersAndOperator(
    eventSelector.selector,
  ).propertyFilters.map((filter) => convertPropertyFilterToCommon(filter));

  const event = getEventFromCohortEvent(eventSelector.event);

  const label = `${event.label} ${getDateRangeLabelFromBehavior(clause.behavior, clause.isNegated())}`;

  const behavior = {
    dateRange: getDateRangeFromBehavior(clause.behavior),
    event,
    filters: additionalFilters,
    filtersOperator: getFiltersOperatorFromPropertyFilterOperator(clause.propertyFilterOperator),
    aggregationOperator: PRIMITIVE_MATH_TYPES.TOTAL,
    property: null,
  };

  // transforms "on unique days" to do a count on distinct days
  if (type === BehaviorCountType.Day) {
    behavior.aggregationOperator = PRIMITIVE_MATH_TYPES.UNIQUE_VALUES;
    behavior.property = {
      label: null,
      value: MPProperties.$Time,
      unit: TimeUnit.Day,
      resourceType: ResourceType.Event,
      propertyDefaultType: PropertyType.Datetime,
      type: PropertyType.Datetime,
    };
  }

  const customProperty: CustomProperty = {
    name: label,
    behavior,
    description: ``,
    referencedBy: [],
    referencedDirectlyBy: [],
    referencedRawEventProperties: [],
    propertyType: PropertyType.Number,
    resourceType: CustomPropertyResourceType.People,
  };

  const behaviorFilter = new CommonPropertyFilter({
    customProperty,
    ...(clause.isNegated()
      ? {
          filterOperator: NumberOperator.EqualTo,
          filterValue: 0,
        }
      : {
          filterOperator: FrequencyOperatorToNumericFilterOperatorMapping[clause.frequencyOperator],
          filterValue: clause.frequency.length === 1 ? clause.frequency[0] : clause.frequency,
        }),
    propertyType: PropertyType.Number,
    propertyName: label,
    resourceType: ResourceType.User,
  });

  return [behaviorFilter];
}

export function getEventFiltersFromPeoplePropertyClause(clause: PeoplePropertyClause): CommonPropertyFilter[] {
  return clause.propertyFilters.map((filter) => convertPropertyFilterToCommon(filter));
}

export function createCohortCommonPropertyFilter({
  cohortId,
  negated = false,
  rawCohort,
}: {
  cohortId?: number | null;
  negated?: boolean;
  rawCohort?: SerializedCohort;
}) {
  let cohort: CohortSegment[ResourceType.Cohort];
  if (cohortId) {
    cohort = {
      id: cohortId,
      negated,
    };
  } else if (rawCohort) {
    cohort = {
      name: rawCohort.name,
      raw_cohort: {
        name: rawCohort.name,
        behaviors: rawCohort.behaviors,
        groups: rawCohort.groups,
        selector: rawCohort.selector,
        unsavedId: rawCohort.unsavedId,
        data_group_id: rawCohort.data_group_id,
        createdBy: rawCohort.created_by,
      },
      negated,
    };
  }
  if (!cohort) {
    assertUnreachable(cohort as never);
  }
  return new CommonPropertyFilter({
    filterValue: [{cohort} as FilterOperand],
    propertyType: PropertyType.List,
    filterOperator: negated ? ListOperator.DoesNotContain : ListOperator.Contains,
    resourceType: ResourceType.Cohort,
  });
}
